import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { fetchOrderFeedbacks } from '@/store/slices/feedbackSlice';
import { ArrowDownTrayIcon, XMarkIcon } from "@heroicons/react/24/solid";
import Toaster from "@/components/Toaster";
import UploadFeedbackModal from './order-modals/UploadFeedbackModal';
import { useTranslation } from 'react-i18next';

const OrderFeedback = ({ rfq_id }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('order');
  const [previewImage, setPreviewImage] = useState(null);

  const user = useSelector((state) => state.user.user);

  const canUploadFeedback =
    user?.roles?.[0] === 'Administrator' ||
    user?.roles?.[0] === 'Sub Admin';
  
  // Destructure the state from Redux
  const { order_feedbacks: feedback, loading, error } = useSelector((state) => state.feedback);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  useEffect(() => {
    if (!rfq_id) return;
    dispatch(fetchOrderFeedbacks({rfq_id}));
  }, [rfq_id, dispatch]);

  const refreshFeedback = () => {
    dispatch(fetchOrderFeedbacks({ rfq_id }));
  };

  const handleDownloadImage = async (imageUrl) => {
    try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;

        const fileName =
            imageUrl.split("/").pop()?.split("?")[0] || "attachment";

        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        Toaster.error(t('failed_to_download_image'));
    }
  };

  const isImageFile = (url) => {
    return /\.(jpg|jpeg|png|webp)$/i.test(url || '');
  };

  const isPdfFile = (url) => {
    return /\.pdf$/i.test(url || '');
  };
  
  // Helper to render the star rating
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, index) => (
          <svg
            key={index}
            className={`w-5 h-5 ${index < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-200'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            stroke={index >= Math.floor(rating) ? 'currentColor' : 'none'}
            strokeWidth={index >= Math.floor(rating) ? '1' : '0'}
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full max-w-6xl bg-white p-6 rounded-lg flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  // FIXED: Safely extract string messages if 'error' is an object from the backend
  if (error) {
    const errorMessage = typeof error === 'object' && error !== null 
      ? error.message || JSON.stringify(error) 
      : error;

    return (
      <div className="w-full max-w-6xl bg-white p-6 rounded-lg text-red-500">
        {t('error_loading_feedback')}: {errorMessage}
      </div>
    );
  }

  // if (!feedback || !feedback.id) {
  //   return (
  //     <div className="w-full max-w-6xl bg-white p-6 rounded-lg text-slate-500 text-center py-10">
  //       {t('no_feedback_available')}
  //     </div>
  //   );
  // }

  if (!feedback || !feedback.id) {
    return (
      <div className="w-full max-w-6xl bg-white p-6 rounded-lg text-center py-10">
        <p className="text-slate-500 mb-4">
          {t('no_feedback_available')}
        </p>

        {canUploadFeedback && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-white hover:bg-grey-500"
          >
            {t('upload_customer_feedback_button')}
          </button>
        )}

        <UploadFeedbackModal
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          rfq_id={rfq_id}
          onSuccess={refreshFeedback}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl bg-white p-6 rounded-lg">
      <div className="space-y-8">
        
        {/* Header: Rating & Customer Info */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <h3 className="text-slate-500 font-bold text-lg">{t('rating_label')}:</h3>
            {renderStars(feedback.star_rating || 0)}
            <span className="text-slate-600 font-medium text-sm ml-1">
              ({Number(feedback.star_rating || 0).toFixed(1)} / 5.0)
            </span>
          </div>
          
          {/* Displaying user info from the API payload */}
          <div className="text-left sm:text-right">
            <div className="text-sm font-semibold text-slate-700">
              {feedback.first_name} {feedback.last_name}
            </div>
            {feedback.registered_company_name && (
              <div className="text-xs text-slate-500">{feedback.registered_company_name}</div>
            )}
            {feedback.created_at && (
              <div className="text-xs text-slate-400 mt-0.5">
                {new Date(feedback.created_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Customer Review Comments Section */}
        <div>
          <h4 className="text-slate-400 font-semibold text-sm mb-3">
            {t('customer_review_comments_label')}:
          </h4>
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 min-h-[80px]">
            <p className="text-slate-700 whitespace-pre-wrap">
              {feedback.description || t('no_comments_provided')}
            </p>
          </div>
        </div>

        {feedback.feedback_proof_file && (
          <div>
            <h4 className="text-slate-400 font-semibold text-sm mb-3">
              {t('customer_feedback_proof_label')}:
            </h4>

            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                  {isPdfFile(feedback.feedback_proof_file) ? (
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H4zm7 1.5L16.5 9H13a2 2 0 01-2-2V3.5z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5zm2 2a1 1 0 100 2 1 1 0 000-2zm2 6l2.5-3 1.5 2 2.5-3L16 13H8z" />
                    </svg>
                  )}
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {feedback.feedback_proof_file.split('/').pop()}
                  </p>
                  <p className="text-xs text-slate-500">
                    {isPdfFile(feedback.feedback_proof_file) ? t('pdf_document_label') : t('image_proof_label')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {isImageFile(feedback.feedback_proof_file) && (
                  <button
                    type="button"
                    onClick={() => setPreviewImage(feedback.feedback_proof_file)}
                    className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-white"
                  >
                    {t('view_button')}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleDownloadImage(feedback.feedback_proof_file)}
                  className="px-3 py-2 rounded-lg bg-black text-white text-sm hover:bg-gray-800 inline-flex items-center gap-2"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {t('download_button')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Uploaded Image Attachments Section */}
        {feedback.images && feedback.images.length > 0 && (
          <div>
            <h4 className="text-slate-400 font-semibold text-sm mb-3">
              {t('uploaded_image_attachments_label')}:
            </h4>
            <div className="flex flex-wrap gap-4">
              {feedback.images.map((imgSrc, imgIndex) => (
                <img 
                  key={imgIndex} 
                  src={typeof imgSrc === 'string' ? imgSrc : imgSrc?.image_url}
                  alt={`Attachment ${imgIndex + 1}`} 
                  className="w-24 h-24 object-cover rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"                  
                  onClick={() => setPreviewImage(typeof imgSrc === 'string' ? imgSrc : imgSrc?.image_url)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {previewImage && (
          <div 
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
              onClick={() => setPreviewImage(null)}
          >
              <div 
                  className="relative max-w-3xl max-h-[85vh] p-2 bg-white rounded-xl shadow-2xl overflow-hidden mx-4 flex flex-col items-center"
                  onClick={(e) => e.stopPropagation()} 
              >
                  <div className="absolute top-3 right-3 flex gap-2 z-50">
                      <button
                          type="button"
                          onClick={() => handleDownloadImage(previewImage)}
                          className="p-2 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors"
                          title={t('download_image_title')}
                      >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>

                      <button
                          type="button"
                          onClick={() => setPreviewImage(null)}
                          className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                          title={t('close_button')}
                      >
                          <XMarkIcon className="h-5 w-5" />
                      </button>
                  </div>
                  <img src={previewImage} alt="Attachment Full Preview" className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
              </div>
          </div>
      )}
    </div>
  );
};

export default OrderFeedback;